import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { getUser } from '../App';

const GARNET = '#5b1e22';
const COAL = '#181617';
const ASHEN = '#7A766F';
const RED = '#b32d2d';
const GREEN = '#1a7a4c';

const COUNTRIES = ['HK', 'SG', 'BKK'];
const COUNTRY_LABEL = { HK: 'Hong Kong', SG: 'Singapore', BKK: 'Bangkok' };

const EMPTY = {
  name: '', country: 'HK', address: '', opening_time: '11:00', closing_time: '20:00',
  slot_duration_minutes: 30, studio_pin: '', is_active: true, shopify_location_id: '',
  is_online_default: false, maps_url: '', google_review_url: '',
};

const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 };
const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, color: COAL, boxSizing: 'border-box' };
const cardStyle = { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: 20, marginBottom: 16 };
const badge = (bg, fg) => ({ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: bg, color: fg, marginLeft: 8, verticalAlign: 'middle' });

function Field({ label, children, hint }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: ASHEN, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function StudioForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  function submit(e) {
    e.preventDefault();
    onSave({
      ...f,
      slot_duration_minutes: parseInt(f.slot_duration_minutes, 10),
      shopify_location_id: f.shopify_location_id === '' ? null : f.shopify_location_id,
    });
  }

  return (
    <form onSubmit={submit}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 14 }}>
        <Field label="Studio name *">
          <input style={inputStyle} value={f.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Labret K11 Musea" required />
        </Field>
        <Field label="Country *">
          <select style={inputStyle} value={f.country} onChange={e => set('country', e.target.value)}>
            {COUNTRIES.map(c => <option key={c} value={c}>{c} — {COUNTRY_LABEL[c]}</option>)}
          </select>
        </Field>
        <Field label="Address" hint="Shown in booking confirmations and reminders">
          <input style={inputStyle} value={f.address || ''} onChange={e => set('address', e.target.value)} placeholder="Street, unit, district" />
        </Field>
        <Field label="Opens">
          <input style={inputStyle} type="time" value={f.opening_time} onChange={e => set('opening_time', e.target.value)} required />
        </Field>
        <Field label="Closes">
          <input style={inputStyle} type="time" value={f.closing_time} onChange={e => set('closing_time', e.target.value)} required />
        </Field>
        <Field label="Slot length (min)" hint="Booking slot size on the calendar">
          <input style={inputStyle} type="number" min={5} max={240} step={5} value={f.slot_duration_minutes} onChange={e => set('slot_duration_minutes', e.target.value)} required />
        </Field>
        <Field label="Tablet PIN" hint="4–6 digits. Check-in + staff tablets use this to bind to the studio. Must be unique.">
          <input style={inputStyle} inputMode="numeric" pattern="\d{4,6}" value={f.studio_pin || ''} onChange={e => set('studio_pin', e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="e.g. 1234" />
        </Field>
        <Field label="Shopify location ID" hint="POS location ID from Shopify → Settings → Locations (number in the URL). Ties this store's sales to it.">
          <input style={inputStyle} inputMode="numeric" value={f.shopify_location_id ?? ''} onChange={e => set('shopify_location_id', e.target.value.replace(/\D/g, ''))} placeholder="e.g. 94929158364" />
        </Field>
        <Field label="Google Maps link">
          <input style={inputStyle} type="url" value={f.maps_url || ''} onChange={e => set('maps_url', e.target.value)} placeholder="https://maps.app.goo.gl/…" />
        </Field>
        <Field label="Google review link" hint="Used by Lily when asking happy customers for a review">
          <input style={inputStyle} type="url" value={f.google_review_url || ''} onChange={e => set('google_review_url', e.target.value)} placeholder="https://g.page/r/…/review" />
        </Field>
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 18 }}>
        <label style={{ fontSize: 14, color: COAL, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!f.is_active} onChange={e => set('is_active', e.target.checked)} />
          Active (bookable, selectable on tablets)
        </label>
        <label style={{ fontSize: 14, color: COAL, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!f.is_online_default} onChange={e => set('is_online_default', e.target.checked)} />
          Online sales default for {f.country}
          <span style={{ fontSize: 11, color: ASHEN }}>(website orders for this country count towards this store — one per country)</span>
        </label>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={saving} style={{ padding: '9px 20px', background: GARNET, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          {saving ? 'Saving…' : (initial?.id ? 'Save changes' : 'Create studio')}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} style={{ padding: '9px 16px', background: 'none', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, color: ASHEN, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function Studios() {
  const user = getUser();
  const canEdit = user?.role === 'owner';
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // null | 'create' | studio id being edited
  const [saving, setSaving] = useState(false);
  const [showPin, setShowPin] = useState({});
  const [toast, setToast] = useState('');
  const [toastError, setToastError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/studios/admin');
      setStudios(res.data.studios || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function showToast(msg, isError = false) {
    setToast(msg); setToastError(isError);
    setTimeout(() => setToast(''), 3500);
  }

  async function handleSave(fields) {
    setSaving(true);
    try {
      if (mode === 'create') {
        const res = await api.post('/studios/admin', fields);
        showToast(res.data.message);
      } else {
        const res = await api.put(`/studios/admin/${mode}`, fields);
        showToast(res.data.message);
      }
      setMode(null);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save studio', true);
    } finally {
      setSaving(false);
    }
  }

  const byCountry = COUNTRIES.map(c => ({ country: c, list: studios.filter(s => s.country === c) })).filter(g => g.list.length > 0);

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', background: toastError ? RED : GREEN, color: '#fff', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, zIndex: 2000, boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
          {toast}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, color: COAL, fontSize: 22 }}>Studios</h2>
          <div style={{ fontSize: 13, color: ASHEN, marginTop: 4 }}>
            Manage every studio centrally — hours, tablet PIN, Shopify POS location, links. New studios appear on the booking calendar and tablets automatically.
          </div>
        </div>
        {canEdit && mode !== 'create' && (
          <button onClick={() => setMode('create')} style={{ padding: '9px 20px', background: GARNET, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Add Studio
          </button>
        )}
      </div>

      {mode === 'create' && (
        <div style={{ ...cardStyle, borderLeft: `4px solid ${GARNET}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: ASHEN, marginBottom: 16 }}>New Studio</div>
          <StudioForm initial={EMPTY} onSave={handleSave} onCancel={() => setMode(null)} saving={saving} />
        </div>
      )}

      {loading ? (
        <div style={{ color: ASHEN, fontSize: 14 }}>Loading…</div>
      ) : studios.length === 0 ? (
        <div style={{ ...cardStyle, color: ASHEN, fontSize: 14 }}>No studios yet.</div>
      ) : byCountry.map(group => (
        <div key={group.country} style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: ASHEN, marginBottom: 12 }}>
            {COUNTRY_LABEL[group.country]} ({group.country}) · {group.list.length} studio{group.list.length === 1 ? '' : 's'}
          </div>
          {group.list.map(s => (
            <div key={s.id} style={{ ...cardStyle, opacity: s.is_active ? 1 : 0.6 }}>
              {mode === s.id ? (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: ASHEN, marginBottom: 16 }}>Edit — {s.name}</div>
                  <StudioForm initial={s} onSave={handleSave} onCancel={() => setMode(null)} saving={saving} />
                </>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: COAL }}>
                      {s.name}
                      {!s.is_active && <span style={badge('#eee', ASHEN)}>Inactive</span>}
                      {s.is_online_default && <span style={badge('#e8f0fb', '#1f4e8c')}>Online default</span>}
                    </div>
                    <div style={{ fontSize: 13, color: ASHEN, marginTop: 4 }}>{s.address || <em>No address set — confirmations will be blank</em>}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px 20px', marginTop: 12, fontSize: 13, color: COAL }}>
                      <div><span style={{ color: ASHEN }}>Hours:</span> {s.opening_time}–{s.closing_time}</div>
                      <div><span style={{ color: ASHEN }}>Slot:</span> {s.slot_duration_minutes} min</div>
                      <div>
                        <span style={{ color: ASHEN }}>Tablet PIN:</span>{' '}
                        {s.studio_pin
                          ? <>
                              <span style={{ fontFamily: 'monospace' }}>{showPin[s.id] ? s.studio_pin : '••••'}</span>
                              <button onClick={() => setShowPin(p => ({ ...p, [s.id]: !p[s.id] }))} style={{ marginLeft: 6, background: 'none', border: 'none', color: GARNET, cursor: 'pointer', fontSize: 12 }}>
                                {showPin[s.id] ? 'hide' : 'show'}
                              </button>
                            </>
                          : <em style={{ color: RED }}>not set — tablets can't bind</em>}
                      </div>
                      <div><span style={{ color: ASHEN }}>Shopify location:</span> {s.shopify_location_id ?? <em style={{ color: RED }}>not set</em>}</div>
                      {s.maps_url && <div><a href={s.maps_url} target="_blank" rel="noreferrer" style={{ color: GARNET }}>Maps ↗</a></div>}
                      {s.google_review_url && <div><a href={s.google_review_url} target="_blank" rel="noreferrer" style={{ color: GARNET }}>Review link ↗</a></div>}
                    </div>
                  </div>
                  {canEdit && (
                    <div>
                      <button onClick={() => setMode(s.id)} disabled={mode !== null} style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '6px 14px', fontSize: 12, color: COAL, cursor: 'pointer' }}>
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
